import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Dropdown, Menu } from 'components/Dropdown';
import { Button } from 'components/Form';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import { ICONS, PFE_ICONS } from 'constants/icons';
import { PERMISSION } from 'constants/logic';
import { schedulePropTypes } from 'constants/propTypes';
import { getClasspassSpotsAllocationRange } from 'helpers/classpass-spots-allocation';
import { selectActiveVenueId, selectUserHasPermission } from 'selectors/general';
import { selectIsDesktop } from 'selectors/ui';

import { actOnOpportunityFromSchedule, addSpot, updateScheduleNetworkAllocation } from '../YieldManagement.saga';
import { ADD_SPOT_ACTION, getAddSpotAction } from './ScheduleDetailV2.helpers';
import messages from './ScheduleDetailV2.messages';
import StatsCard, { statsCardElement as element } from './StatsCard';

const EMPTY_CLASSPASS_TOTAL = 0;
const EMPTY_CLASSPASS_DEMAND = 0;
const NO_RECOMMENDATION = 0;
const MAX_BOOKINGS_TOOLTIP_NAME_PREFIX = 'booking-overview-max-bookings';
const NETWORK_CAP_FORM_NAME_PREFIX = 'schedule-detail-update-network-cap';

const SpotsPickerToggle = ({ disabled, isOpen, label, toggleMenu, value }) => (
  <button
    aria-label={label}
    className={element('picker-toggle')}
    data-cypress="booking-overview-spots-picker-toggle"
    disabled={disabled}
    onClick={toggleMenu}
    type="button">
    {value}
    <SvgArrow
      className={element('picker-caret')}
      direction={isOpen ? 'up' : 'down'}
      theme={disabled ? 'disabled' : 'dark'}
    />
  </button>
);

SpotsPickerToggle.propTypes = {
  disabled: PropTypes.bool,
  isOpen: PropTypes.bool,
  label: PropTypes.string,
  toggleMenu: PropTypes.func,
  value: PropTypes.number,
};

const ClassPassBookingsCard = ({ schedule }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const venueId = useSelector(selectActiveVenueId);
  const isDesktop = useSelector(selectIsDesktop);
  const hasSpotManagePermission = useSelector(state =>
    selectUserHasPermission(state, PERMISSION.DISPLAY_SPOT_MANAGEMENT)
  );
  const [opportunityActedOn, setOpportunityActedOn] = useState(false);
  const [pendingSpots, setPendingSpots] = useState(null);

  const { isInProgress, isInThePast, opportunity, reservationsStopped, scheduleId, smartspotEnabled, spots } = schedule;
  const {
    classpassBooked = EMPTY_CLASSPASS_TOTAL,
    classpassDemand = EMPTY_CLASSPASS_DEMAND,
    classpassTotal = EMPTY_CLASSPASS_TOTAL,
    smartspotRecommendedSpots,
  } = spots || {};
  const { recommendation = NO_RECOMMENDATION } = opportunity || {};
  const tooltipName = `${MAX_BOOKINGS_TOOLTIP_NAME_PREFIX}-${scheduleId}`;
  const maxBookingsTooltipMessage = smartspotEnabled
    ? messages.maxBookingsTooltipSmartspot
    : messages.maxBookingsTooltipStandard;
  const hasMaxBookings = classpassTotal !== EMPTY_CLASSPASS_TOTAL;
  const showSpotsPicker =
    hasSpotManagePermission && !isInThePast && !isInProgress && !smartspotEnabled && !reservationsStopped;
  const isUpdatingSpots = pendingSpots !== null;
  const displayedTotal = isUpdatingSpots ? pendingSpots : classpassTotal;
  const recommendedSpots = (recommendation && classpassTotal + recommendation) || smartspotRecommendedSpots || null;

  const addSpotAction = getAddSpotAction(schedule, { hasSpotManagePermission, opportunityActedOn });
  const showDemand = classpassDemand > EMPTY_CLASSPASS_DEMAND && Boolean(addSpotAction);

  const settleSpotsUpdate = () => setPendingSpots(null);

  const handleSelectSpots = classpassSpots => {
    if (classpassSpots === classpassTotal) return;

    setPendingSpots(classpassSpots);
    dispatch(
      updateScheduleNetworkAllocation(
        venueId,
        {
          actedOnOpportunity: false,
          classpassSpots,
          opportunities: opportunity ? [opportunity] : [],
          schedule,
          smartspotEnabled,
          spotsAdded: recommendation,
        },
        isDesktop,
        `${NETWORK_CAP_FORM_NAME_PREFIX}-${scheduleId}`,
        { rejectSubmit: settleSpotsUpdate, resolveSubmit: settleSpotsUpdate }
      )
    );
  };

  const handleAddSpot = () => {
    dispatch(addSpot(classpassTotal, scheduleId, classpassTotal + 1, smartspotEnabled, showDemand));
  };

  const handleActOnOpportunity = () => {
    dispatch(
      actOnOpportunityFromSchedule({
        className: schedule.scheduleName,
        opportunities: [opportunity],
        schedule,
        showDemand,
        source: 'schedule detail',
        spotsAdded: addSpotAction.recommendation,
        startDateTime: schedule.startDateTime,
        venueId,
      })
    );
    setOpportunityActedOn(true);
  };

  const renderSpotsMenuItem = text =>
    text === recommendedSpots ? (
      <span className={element('picker-recommendation')}>
        {text}
        <Isvg className={element('picker-recommendation-icon')} src={PFE_ICONS.opportunitiesFill} />
      </span>
    ) : (
      text
    );

  const spotsPicker = (
    <Dropdown
      attachment="left"
      className={element('picker')}
      closeOnScroll
      Content={Menu}
      dataCypress="booking-overview-spots-picker"
      disabled={isUpdatingSpots}
      isSelected={option => option === displayedTotal}
      items={getClasspassSpotsAllocationRange(schedule)}
      label={intl.formatMessage(messages.maxBookings, { count: displayedTotal })}
      onSelect={handleSelectSpots}
      renderMenuItem={renderSpotsMenuItem}
      Toggle={SpotsPickerToggle}
      value={displayedTotal}
    />
  );

  const muted = text => <span className={element('value-muted')}>{text}</span>;
  const span = text => <span>{text}</span>;

  let value = <span>{classpassBooked}</span>;
  if (!smartspotEnabled) {
    value = showSpotsPicker ? (
      <Message
        message={messages.classPassBookingsPickerFraction}
        values={{ muted, picker: spotsPicker, span, x: classpassBooked }}
      />
    ) : (
      <Message
        message={messages.classPassBookingsFraction}
        values={{ muted, span, x: classpassBooked, y: classpassTotal }}
      />
    );
  }

  const footer = (
    <>
      <div className={element('footer-meta')}>
        {hasMaxBookings ? (
          <Message message={messages.maxBookings} values={{ count: classpassTotal }} />
        ) : (
          <Message message={messages.maxBookingsUnavailable} />
        )}
        <TooltipToggle name={tooltipName}>
          <Isvg className={element('tooltip-icon')} src={ICONS.infoGrey} />
        </TooltipToggle>
        <TooltipContent className={element('tooltip-content')} hoverable name={tooltipName} place="top">
          <Message message={maxBookingsTooltipMessage} />
        </TooltipContent>
      </div>
      {addSpotAction && (
        <Button
          className={element('footer-action')}
          styled="inline-link"
          data-cypress="schedule-detail-v2-add-spot"
          onClick={addSpotAction.type === ADD_SPOT_ACTION.OPPORTUNITY ? handleActOnOpportunity : handleAddSpot}>
          {addSpotAction.type === ADD_SPOT_ACTION.OPPORTUNITY ? (
            <Message message={messages.addNSpots} values={{ recommendation: addSpotAction.recommendation }} />
          ) : (
            <Message message={messages.addSpot} />
          )}
        </Button>
      )}
    </>
  );

  return (
    <StatsCard
      dataCypress="booking-overview-classpass-card"
      footer={footer}
      label={<Message message={messages.classPassBookingsLabel} />}
      value={value}
    />
  );
};

ClassPassBookingsCard.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes).isRequired,
};

export default ClassPassBookingsCard;
